  <template>
    <div
      v-if="props.itemData && showStatus"
      ref="entryEffectRef"
      :id="props.itemData.config.id"
      :data-target-level-keys="disabled ? undefined : itemIdxStr"
      :data-target-level-disabled-keys="disabled ? itemIdxStr : undefined"
      :data-can-move-up="(idx > 0) + ''"
      :data-can-move-down="(idx < listLength - 1) + ''"
      :data-component-name="componentName"
      :class="[
        'element-item-all-wrap',
        'element-item-wrap',
        filterConfig?.classStyle || props.itemData.config.classStyle,
        {
          current: !disabled && currentElementIndexStr == itemIdxStr,
          'element-item-wrap-edit': !disabled,
          'element-item-wrap-disabled': disabled,
        },
      ]"
      :style="mergedStyle"
      @click.capture="handleTrackingClick"
      @click="handleLowCodePopupClick"
      @mouseenter="hoverEffectEvents.mouseenter"
      @mouseleave="hoverEffectEvents.mouseleave"
    >
      <ClientOnly v-if="shouldRenderClientOnly">
        <component
          :disabled="disabled"
          :is="componentName"
          :allArcListMap="allArcListMap"
          :parentKeys="parentKeys"
          :idx="idx"
          :currentElementIndex="currentElementIndex"
          :config="filterConfig"
          :pages="pages"
          @resize="onResizeBox"
        />
      </ClientOnly>
      <component
        v-else
        :disabled="disabled"
        :is="componentName"
        :allArcListMap="allArcListMap"
        :parentKeys="parentKeys"
        :idx="idx"
        :currentElementIndex="currentElementIndex"
        :config="filterConfig"
        :pages="pages"
        @resize="onResizeBox"
      />
    </div>
  </template>
  
  <script setup>
  import { computed, inject, reactive } from 'vue';
  import { useElementIndex } from './uses/elementIndexUse'; // 引入composable
  import { useEntryEffect } from './uses/useEntryEffect.js';
  import { useHoverEffect } from './uses/useHoverEffect.js';
  import { isDisabledMallSsr } from '@/utils/globalData';
  import bus from '@/bus/index';
  import { buildCommonBoxStyle } from './uses/useResponsiveConfig.js';
  import { useLowCodeActionRuntime } from '@/composables/useLowCodeActionRuntime.js';
  import { isLowCodePopupPageAction } from '@/utils/lowCodePopupPage.js';
  import { trackLowCodeEvent } from '@/utils/lowCodeTracking.js';
  
  // 定义 props
  const props = defineProps({
    allArcListMap: { type: Object, default: () => ({}) },
    currentElementIndex: { type: Array, default: () => [] },
    parentKeys: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: true },
    pages: { type: Array, default: () => [] },
    itemData: { type: Object },
    current: { type: Boolean, default: false },
    idx: { type: Number, default: -1 },
    listLength: { type: Number, default: 0 },
  });

  const { dispatchLowCodeAction } = useLowCodeActionRuntime();
  const getConfigJson = inject('getConfigJson', () => ({}));

  const handleTrackingClick = () => {
    if (!props.disabled) return;
    const element = filterConfig.value || props.itemData?.config || {};
    const page = typeof getConfigJson === 'function' ? getConfigJson() : {};
    trackLowCodeEvent(element.trackingConfig, {
      trigger: 'click',
      element,
      elementType: props.itemData?.type || componentName.value,
      elementIndex: itemIdxStr.value,
      page,
      binding: page?.bindData || {},
    });
  };

  const handleLowCodePopupClick = (event) => {
    const action = props.itemData?.config?.clickAction;
    if (!props.disabled || !isLowCodePopupPageAction(action)) return;
    event?.preventDefault?.();
    event?.stopPropagation?.();
    dispatchLowCodeAction(
      action,
      typeof getConfigJson === 'function' ? getConfigJson() : {},
    );
  };
  
  // 引用 composable
  const {
    platformType,
    renderData,
    rectBox,
    filterConfig,
    componentName,
    setStyle,
    showStatus,
    currentElementIndexStr,
    itemIdxStr,
    isPosition,
    selectElement,
    removeElement,
    copyElement,
    moveElementUp,
    moveElementDown,
    setRenderData,
  } = useElementIndex(props);

  const clientOnlyMallComponentSet = new Set([
    'ProductDetail1688Item',
    'MallGoodsCard',
  ]);

  const shouldRenderClientOnly = computed(() => {
    return isDisabledMallSsr() && clientOnlyMallComponentSet.has(componentName.value);
  });

  const { targetRef: entryEffectRef, entryEffectStyle } = useEntryEffect(
    () => filterConfig.value || props.itemData?.config || {},
  );
  const { hoverEffectEvents, wrapperHoverStyle, contentHoverStyle } = useHoverEffect(
    () => filterConfig.value || props.itemData?.config || {},
  );
  
  // 计算样式
  const mergedStyle = computed(() => {
    const config = filterConfig.value || props.itemData.config || {};
    const commonBoxStyle = buildCommonBoxStyle(config, {}, {
      includeOpacity: false,
      includeZIndex: false,
    });
    return {
      ...commonBoxStyle,
      position: isPosition.value ? config.position : undefined,
      left: isPosition.value && config.positionVal?.left !== 'auto' ? `${config.positionVal.left}px` : undefined,
      top: isPosition.value && config.positionVal?.top !== 'auto' ? `${config.positionVal.top}px` : undefined,
      right: isPosition.value && config.positionVal?.right !== 'auto' ? `${config.positionVal.right}px` : undefined,
      bottom: isPosition.value && config.positionVal?.bottom !== 'auto' ? `${config.positionVal.bottom}px` : undefined,
      zIndex:
        typeof config.zIndex === 'number' ? String(config.zIndex) : undefined,
      opacity:
        typeof config.opacity === 'number' ? String(config.opacity) : undefined,
      ...entryEffectStyle.value,
      ...wrapperHoverStyle.value,
      ...contentHoverStyle.value,
      ...setStyle.value,
    };
  });
  
  // 事件监听
  const onResizeBox = (e) => {
    console.log('resize', e);
  };
  
  const onBgImageLoad = () => {
    console.log('bg image loaded');
  };
  </script>
  
  <style scoped>
  /* 样式与原本相同 */
  .element-item-wrap {
    position: relative;
    /* overflow: hidden; */
    background-size: 100% auto;
    background-position: center top;
  } 
  .element-item-wrap-edit:hover::before{
    display: block;
  }
 
  .element-item-wrap-edit::before {
    content: " ";
    position: absolute;
    left: 0;
    display: none;
    top: 0px;
    right: 0;
    bottom: 0;
    z-index: 1112;
    pointer-events: none;
    user-select: none;
    border: 1px dashed rgba(0, 0, 0, 0.3);
  }
  .element-item-wrap-edit.current::before{
    display: block;
    border:1px solid #4169e1 !important;
 
  }
  .element-item-all-wrap{
     
  }
  </style>
  
